// pages/forms/formBuilderNormalizers.js
//
// Pure, dependency-free helpers extracted out of FormBuilderPage.jsx so
// they're unit-testable on their own -- FormBuilderPage.jsx pulls in
// react-router-dom, which this project's Jest environment currently can't
// resolve (a pre-existing, unrelated issue; see src/App.test.js), so any
// test file that imports FormBuilderPage.jsx directly fails before it can
// even run. Keeping this logic here means its regression tests aren't
// blocked by that.

// Legacy field_type compatibility map -- some existing draft forms
// (created before validators/formFieldValidator.js's canonical FIELD_TYPES
// registry settled on PROJECT_SELECT/PROJECT_MODULE_SELECT) still have
// rows stored with pre-canonical type names. The backend validator only
// knows the canonical names and rejects these outright at publish time
// ("field_type \"PROJECT_DROPDOWN\" is not supported"). This is a pure
// rename applied when the builder loads the field into memory -- id,
// field_key, label, is_required, display_order, scoring settings and
// section membership all pass through untouched; nothing is deleted,
// recreated, or duplicated. The rename only reaches the database once the
// admin clicks Save Structure, which PUTs this same field row (by its
// existing id) through the normal fields API -- an UPDATE, not an insert.
export const LEGACY_FIELD_TYPE_MAP = {
  PROJECT_DROPDOWN: "PROJECT_SELECT",
  MODULE_DROPDOWN: "PROJECT_MODULE_SELECT",
};

// The only scoring_mode values validators/formFieldValidator.js's
// SCORING_MODES accepts. Kept in sync by hand -- this file intentionally
// has zero imports so it stays testable without pulling in react-router-dom
// (see file header).
export const SCORING_MODES = ["NONE", "AUTO", "MANUAL", "AUTO_WITH_REVIEW"];

// Some existing draft fields carry a pre-canonical scoring_mode value (e.g.
// "NO", "YES", "TRUE", "1", "") left over from before SCORING_MODES was
// settled -- the backend rejects these outright at publish time
// ("scoring_mode must be one of: NONE, AUTO, MANUAL, AUTO_WITH_REVIEW").
// Rather than guess at a legacy value's intended meaning in isolation, the
// field's OWN score_enabled flag is the safe signal: a field that isn't
// scored can only ever mean NONE; a scored field with a garbage mode is
// safely defaulted to AUTO (the common case -- e.g. RATING fields), never
// silently left as something publish will just reject again. An already-
// canonical MANUAL/AUTO_WITH_REVIEW value on a scored field is preserved
// exactly -- this never overwrites a deliberate choice.
export function normalizeScoringMode(scoringMode, scoreEnabled) {
  if (!scoreEnabled) return "NONE";
  if (SCORING_MODES.includes(scoringMode)) return scoringMode;
  return "AUTO";
}

export function toBuilderSection(raw, onLegacyFieldType, onLegacyScoringMode) {
  return {
    id: raw.id,
    title: raw.title,
    description: raw.description,
    display_order: raw.display_order,
    is_repeatable: !!raw.is_repeatable,
    minimum_repetitions: raw.minimum_repetitions,
    maximum_repetitions: raw.maximum_repetitions,
    score_enabled: !!raw.score_enabled,
    weightage: raw.weightage,
    minimum_passing_score: raw.minimum_passing_score,
    fields: (raw.fields || []).map((f) => {
      const canonicalType = LEGACY_FIELD_TYPE_MAP[f.field_type];
      if (canonicalType) onLegacyFieldType?.(f.field_key, f.field_type, canonicalType);

      const scoreEnabled = !!f.score_enabled;
      const canonicalScoringMode = normalizeScoringMode(f.scoring_mode, scoreEnabled);
      if (canonicalScoringMode !== f.scoring_mode) {
        onLegacyScoringMode?.(f.field_key, f.scoring_mode, canonicalScoringMode);
      }

      return {
        ...f,
        field_type: canonicalType || f.field_type,
        score_enabled: scoreEnabled,
        scoring_mode: canonicalScoringMode,
        conditional_visibility_json: typeof f.conditional_visibility_json === "string" ? (f.conditional_visibility_json ? JSON.parse(f.conditional_visibility_json) : null) : f.conditional_visibility_json,
        calculation_config_json: typeof f.calculation_config_json === "string" ? (f.calculation_config_json ? JSON.parse(f.calculation_config_json) : null) : f.calculation_config_json,
        options: f.options || [],
      };
    }),
  };
}
